import { withResolvers } from '@cian/core/utils/shared/promise.js';
import { HttpCancelError } from '@cian/peperrors';

/**
 * Интерфейс отмены запросов
 *
 * @export
 * @interface ICancellationToken
 * @extends {AbortController}
 */
export interface ICancellationToken extends AbortController {
  /**
   * Отмена запроса {@linkcode AbortController.abort}
   *
   * @param {HttpCancelError} [error]
   * @memberof ICancellationToken
   */
  cancel(error?: HttpCancelError): void;
  /**
   * Состояние сигнала {@linkcode AbortSignal.aborted}
   *
   * @return {AbortSignal['aborted']}
   * @memberof ICancellationToken
   */
  isCanceled(): AbortSignal['aborted'];
  /**
   * Возвращает промис отмены запроса
   *
   * @return {(Promise<HttpCancelError | void>)}
   * @memberof ICancellationToken
   */
  toPromise(): Promise<HttpCancelError | void>;
  /**
   * Уничтожает промис
   *
   * @memberof ICancellationToken
   */
  destroy(): void;
  /**
   * Причина отмены {@linkcode AbortSignal['reason']}
   *
   * @type {HttpCancelError}
   * @memberof ICancellationToken
   */
  reason?: HttpCancelError;
}

/**
 * Релизация {@linkcode ICancellationToken}
 *
 * @export
 * @class CancellationToken
 * @extends {AbortController}
 * @implements {ICancellationToken}
 */
export class CancellationToken extends AbortController implements ICancellationToken {
  private promise: Promise<HttpCancelError>;
  private resolve: (error?: HttpCancelError) => void;

  public constructor() {
    super();

    const { promise, resolve } = withResolvers<HttpCancelError>();

    this.promise = promise;
    this.resolve = resolve;
  }

  public isCanceled(): AbortSignal['aborted'] {
    return this.signal.aborted;
  }

  public get reason(): HttpCancelError | undefined {
    return this.signal.reason;
  }

  public cancel(error?: HttpCancelError) {
    if (this.signal.aborted) {
      return;
    }

    const err = error || new HttpCancelError('Canceled HTTP Request', 'http-api.CancellationToken');

    this.abort(err);
    this.resolve(err);
  }

  public toPromise() {
    return this.promise;
  }

  public destroy() {
    if (this.isCanceled()) {
      return;
    }

    this.resolve();
  }
}
